import { useCallback, useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import type { State, Quota } from '../api.js';
import { api } from '../api.js';
import { Hero } from './Hero.js';
import { TileRow } from './TileRow.js';
import { ActionRow } from './ActionRow.js';
import { HistoryChart } from './HistoryChart.js';
import { RuntimeSummaryTile } from './RuntimeSummaryTile.js';
import { useOutletContext } from 'react-router';
import type { ShellContext } from './AppShell.js';
import { SetTempSheet } from './SetTempSheet.js';
import { PresenceSheet } from './PresenceSheet.js';
import { nextBand, tokensFor, type Band } from '../palette.js';
import { t, formatAgo } from '../i18n.js';

type SheetKind = null | 'set-temp' | 'presence';

export function TemperatureSection() {
  const [state, setState] = useState<State | null>(null);
  const [quota, setQuota] = useState<Quota | null>(null);
  const [sheet, setSheet] = useState<SheetKind>(null);
  const [band, setBand] = useState<Band>('low');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const { onOpenSettings } = useOutletContext<ShellContext>();

  const reload = useCallback(async () => {
    try {
      const [s, q] = await Promise.all([api.getState(), api.getQuota()]);
      setState(s);
      setQuota(q);
      setBand((prev) => nextBand(prev, s.zone?.heating_power ?? null));
      setErr(null);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void reload();
    const t = setInterval(() => void reload(), 60_000);
    return () => clearInterval(t);
  }, [reload]);

  if (!state || !quota) {
    return <div className="app"><div className="center-state">{t('app.loading')}</div></div>;
  }

  const refreshDisabled = quota.remaining != null && quota.remaining < 6;

  const wrap = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try { await fn(); await reload(); }
    finally { setBusy(false); }
  };

  const lastUpdated = state.fetched_at
    ? formatAgo(Math.floor(Date.now() / 1000) - state.fetched_at)
    : t('app.never');

  const tokens = tokensFor(band);
  const themeStyle: CSSProperties = {
    ['--hero-bg' as string]: tokens.bgGradient,
    ['--hero-ink' as string]: tokens.ink,
    ['--hero-temp-gradient' as string]: tokens.tempGradient,
    ['--hero-glow' as string]: tokens.glow,
    ['--hero-bar-gradient' as string]: tokens.barGradient,
    ['--btn-gradient' as string]: tokens.btnGradient,
    ['--btn-shadow' as string]: tokens.btnShadow,
  };

  return (
    <div className="app" style={themeStyle}>
      <header className="shell-header">
        <div>
          <span className="updated">{t('header.updated', { ago: lastUpdated })}</span>
        </div>
        <div className="right">
          <span className="quota-pill">
            {quota.remaining == null
              ? t('header.calls_left_unknown')
              : t('header.calls_left', { n: quota.remaining.toLocaleString() })}
          </span>
          <button
            className="refresh-pill"
            onClick={() => void wrap(() => api.refreshNow())}
            disabled={refreshDisabled || busy}
            aria-label={t('header.refresh')}
            title={refreshDisabled ? t('header.refresh_disabled') : t('header.refresh_title')}
          ><span className="icon">↻</span> {t('header.refresh')}</button>
          <button className="hero-gear" onClick={onOpenSettings} aria-label={t('header.settings')}>⚙</button>
        </div>
      </header>
      {err && <div className="banner">{err}</div>}
      <div className="app-body">
        <div className="app-top">
          <Hero
            state={state}
            band={band}
            onEndOverride={() => void wrap(() => api.endOverride())}
            onOpenSettings={onOpenSettings}
            onRefresh={() => void wrap(() => api.refreshNow())}
            refreshDisabled={refreshDisabled || busy}
          />
          <div className="app-top-side">
            <TileRow
              state={state}
              onPresenceTap={() => setSheet('presence')}
            />
            <ActionRow
              onSetTemperature={() => setSheet('set-temp')}
              busy={busy}
            />
          </div>
        </div>
        <RuntimeSummaryTile />
        <HistoryChart />
      </div>

      {sheet === 'set-temp' && (
        <SetTempSheet
          initial={state.zone?.target_temp ?? 20}
          onClose={() => { setSheet(null); void reload(); }}
        />
      )}
      {sheet === 'presence' && (
        <PresenceSheet
          state={state}
          onClose={() => { setSheet(null); void reload(); }}
        />
      )}

    </div>
  );
}

